import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  VStack,
  Image,
  HStack,
  Button,
  Flex,
  Grid,
  SimpleGrid,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import OurMission from "../AboutUs/OurMission";
import OurLeaders from "./OurLeaders";

const AboutBusinessAutomation = ({ data }) => {
  // State for expandable text
  const [isExpanded, setIsExpanded] = useState(true);

  // Get base URL from environment variable
  const siteBaseUrl = process.env.REACT_APP_SITE_BASE_URL;

  // Dynamic data from API
  const keyValues =
    data?.iconImages?.slice(0, 3)?.map((image) => ({
      icon: image?.url,
      title: image?.alternativeText || image?.name || "Feature",
      color: "#000B8C",
      isImage: true,
    })) || [];

  const iconImage =
    data?.iconImages?.slice(3)?.map((image) => ({
      icon: image?.url,
      title: image?.alternativeText || image?.name || "Icon",
      color: "#000B8C",
      isImage: true,
    })) || [];

  // const statsCards =
  //   data?.cardsItems?.map((card) => ({
  //     title: card?.title,
  //     subtitle: card?.description,
  //     icon: FaCalendarAlt,
  //     bgGradient: "linear(to-r, #000B8C, blue.800)",
  //   })) || [];

  const navigate = useNavigate();

  return (
    <Box
      py={{ base: 4, md: 6, lg: 8 }}
      sx={{
        backgroundImage: data?.backgroundImage?.url
          ? `url(${data.backgroundImage.url})`
          : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Box>
        <Container maxW="8xl" px={{ base: 8, md: 16 }}>
          <Breadcrumb
            spacing="8px"
            separator={<ChevronRightIcon color="gray.500" />}
            fontSize="sm"
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                color="#000B8C"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate("/")}
                cursor="pointer"
              >
                Home
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <Text color="gray.600">About Us</Text>
            </BreadcrumbItem>
          </Breadcrumb>
        </Container>
      </Box>
      <Container maxW="7xl" px={{ base: 4, md: 6, lg: 8 }}>
        <VStack align="center" spacing={{ base: 2, md: 4 }}>
          <Heading
            as="h1"
            size={{ base: "xl", md: "2xl" }}
            color="#000B8C"
            fontWeight="bold"
            lineHeight="shorter"
            mb={0}
          >
            Our Story
          </Heading>
          <Text
            fontStyle="italic"
            color="gray.500"
            fontSize={{ base: "md", md: "lg" }}
            fontWeight="medium"
            mt={0}
            mb={3}
          >
            Trusted Technology Partner. Future-Ready. Human-Centered.
          </Text>
          <VStack spacing={6} align="stretch" w="full">
            {/* Paragraph 1 - Always visible */}
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              // paddingRight={{ base: 0, md: 2, lg: 4 }}
              lineHeight="1.2"
              textAlign={"justify"}
            >
              Founded in 1998, Business Automation Limited emerged as one of
              Bangladesh's first IT-enabled service (ITES) providers, driven by
              a bold vision: to empower organizations with world-class digital
              solutions tailored to local needs. Over 27 years, we've grown into
              a trusted name in eGovernment innovation, serving over 300 public
              and private sector clients across Bangladesh and beyond.
            </Text>

            {/* Expandable Content - Paragraphs 2-5 */}

            {/* Paragraph 2 */}
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              // paddingRight={{ base: 0, md: 2, lg: 4 }}
              lineHeight="1.2"
              textAlign={"justify"}
            >
              Our solutions—from enterprise software and citizen service portals
              to IoT-powered systems and workflow automation—enhance service
              delivery, boost operational efficiency, and drive inclusive
              digital growth. Headquartered in Dhaka, we deliver end-to-end
              platforms through in-house research and development, modern UI/UX
              labs, and a robust after-care ecosystem. Our multidisciplinary
              team including women and young professionals, has deployed
              impactful systems, including smart kiosks, customer service
              management tools and national-scale automation platforms that
              serve millions.
            </Text>
            {isExpanded && (
              <>
                {/* Paragraph 3 */}
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  // paddingRight={{ base: 0, md: 2, lg: 4 }}
                  lineHeight="1.2"
                  textAlign={"justify"}
                >
                  We believe intelligent automation is the future of public and
                  enterprise services. That's why we integrate Artificial
                  Intelligence (AI), Robotic Process Automation (RPA), and
                  advanced analytics into our platforms. Our ongoing investment
                  in machine learning and natural language processing (NLP)
                  ensures our platforms evolve with our clients, anticipating
                  needs and delivering seamless experiences.
                </Text>

                {/* Paragraph 4 */}
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  // paddingRight={{ base: 0, md: 2, lg: 4 }}
                  lineHeight="1.2"
                  textAlign={"justify"}
                >
                  As an ISO 9001, ISO 20000 and ISO 27000 certified
                  organization, we uphold excellence in every project, from
                  local SMEs to national digital transformation initiatives. Our
                  inclusive workplace empowers women and young professionals
                  through mentorship, flexible hours, and AI/RPA training,
                  fostering a diverse team that reflects Bangladesh's talent.
                </Text>

                {/* Paragraph 5 */}
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  // paddingRight={{ base: 0, md: 2, lg: 4 }}
                  lineHeight="1.2"
                  textAlign={"justify"}
                >
                  Whether enabling a government agency or partnering with global
                  organizations, Business Automation brings the right talent,
                  tools, and vision to turn challenges into opportunities.
                </Text>
              </>
            )}

            {/* See More / See Less Button */}
            {/* <Button
              variant="link"
              color="#000B8C"
              fontSize={{ base: "sm", md: "md" }}
              fontWeight="medium"
              mt={2}
              p={0}
              h="auto"
              _hover={{ textDecoration: "underline" }}
              onClick={() => setIsExpanded(!isExpanded)}
              alignSelf="flex-start"
            >
              {isExpanded ? "Show Less" : "See More"}
            </Button> */}
          </VStack>
          <Flex
            direction="row"
            align="center"
            justify="center"
            wrap="wrap"
            gap={{ base: 6, md: 10, lg: 12 }}
            my={{ base: 6, md: 10 }}
            w="full"
          >
            {keyValues.map((value, index) => (
              <Box key={`keyValue-${index}`} minW="fit-content" display="flex" alignItems="center" justifyContent="center">
                <Image
                  src={value.icon}
                  alt={value.title}
                  height={{ base: 16, md: 24, lg: 28 }}
                  width="auto"
                  objectFit="contain"
                />
              </Box>
            ))}
            {iconImage.map((value, index) => (
              <Box key={`iconImage-${index}`} minW="fit-content" display="flex" alignItems="center" justifyContent="center">
                <Image
                  src={value.icon}
                  alt={value.title}
                  height={{ base: 16, md: 24, lg: 28 }}
                  width="auto"
                  objectFit="contain"
                />
              </Box>
            ))}
          </Flex>
          </VStack>

          {/* YouTube Video Section */}
        {/* <Box
          w="full"
          maxW="7xl"
          mx="auto"
          my={8}
          bg="white"
          borderRadius="lg"
          overflow="hidden"
          boxShadow="lg"
        >
          <Box
            position="relative"
            paddingBottom="56.25%"
            height="0"
            overflow="hidden"
          >
            <iframe
              src="https://www.youtube.com/embed/rQJAnzANgqs?autoplay=1&mute=1&rel=0&showinfo=0&modestbranding=1&controls=1"
              title="BA Systems Video"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </Box>
        </Box> */}

        {/* Shaping Future Section - Static Content */}
        <Text
          fontSize={{ base: "lg", md: "3xl" }}
          color="#000B8C"
          lineHeight="1.2"
          fontWeight="bold"
          pt={8}
          textAlign="center"
          mb={4}
        >
          <Text as="span" color="#000B8C">
            Shaping
          </Text>{" "}
          <Text as="span" color="gray.700">
            the Future Together
          </Text>
        </Text>
        <Text
          fontSize={{ base: "md", md: "lg" }}
          color="gray.600"
          pb={4}
          lineHeight="1.2"
          textAlign="start"
          maxW="7xl"
          mx="auto"
        >
          We're not just building software—we're shaping the digital
          infrastructure of tomorrow. As Bangladesh moves toward, we are proud
          to stand at the forefront of this transformation, delivering solutions
          that matter and creating opportunities for a smarter, more inclusive
          future.
        </Text>
        <Text
          fontSize={{ base: "md", md: "lg" }}
          color="gray.600"
          lineHeight="1.2"
          textAlign="start"
          maxW="7xl"
          mx="auto"
          fontWeight="medium"
        >
          Let's build that future—together.
        </Text>
        <Text
          fontSize={{ base: "md", md: "lg" }}
          color="gray.600"
          pb={6}
          lineHeight="1.2"
          textAlign="start"
          maxW="7xl"
          mx="auto"
        >
          Partner with us. Work with us. Grow with us.
        </Text>
        <Text
          fontSize={{ base: "md", md: "lg" }}
          color="gray.600"
          pb={6}
          lineHeight="1.2"
          textAlign="center"
          maxW="7xl"
          mx="auto"
          fontStyle="italic"
        >
          Join us to shape a future where technology drives trust, efficiency,
          and inclusion.
        </Text>
      </Container>
      <HStack
        spacing={4}
        pb={6}
        flexDirection={{ base: "column", sm: "row" }}
        w="full"
        justify="center"
        align="center"
      >
        <Button
          size={"md"}
          bg="#000B8C"
          color="white"
          _hover={{ bg: "blue.700" }}
          _active={{ bg: "blue.800" }}
          px={{ base: 6, md: 8 }}
          py={{ base: 4, md: 6 }}
          borderRadius="md"
          onClick={() => navigate("/our-services")}
          w={{ base: "full", sm: "auto" }}
          minW="180px"
        >
          {data?.buttons?.[0]?.text || "Explore Our Solutions"}
        </Button>
        <Button
          size={"md"}
          bg="orange.500"
          color="white"
          _hover={{ bg: "orange.600" }}
          _active={{ bg: "orange.700" }}
          px={{ base: 6, md: 8 }}
          py={{ base: 4, md: 6 }}
          borderRadius="md"
          onClick={() =>
            window.open(
              `${siteBaseUrl}/partnerships-collaborations`,
              "_blank",
              "noopener,noreferrer"
            )
          }
          w={{ base: "full", sm: "auto" }}
          minW="180px"
        >
          {data?.buttons?.[1]?.text || "Partner with Us"}
        </Button>
        <Button
          size={"md"}
          bg="transparent"
          color="#000B8C"
          border="2px solid"
          borderColor="#000B8C"
          _hover={{ bg: "blue.50", borderColor: "blue.700" }}
          _active={{ bg: "blue.100" }}
          px={{ base: 6, md: 8 }}
          py={{ base: 4, md: 6 }}
          borderRadius="md"
          onClick={() => navigate("/careers")}
          w={{ base: "full", sm: "auto" }}
          minW="180px"
        >
          {data?.buttons?.[2]?.text || "Join Our Team"}
        </Button>
      </HStack>

      <Container maxW="7xl" px={{ base: 4, md: 6, lg: 8 }}>
        <Text
          fontSize={{ base: "lg", md: "3xl" }}
          color="#000B8C"
          lineHeight="1.2"
          fontWeight="bold"
          pt={12}
          textAlign="center"
          mb={4}
        >
          <Text as="span" color="#000B8C">
            Milestones{" "}
          </Text>{" "}
          <Text as="span" color="gray.700">
            Timeline
          </Text>
        </Text>
        <Box
          flex={{ base: 1, lg: 1 }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="100%"
        >
          <Image
            src="/png/about_us_1.png"
            alt="About Us Illustration"
            maxW="100%"
            height="auto"
            borderRadius="lg"
            loading="lazy"
            fallbackSrc="/png/default-product.png"
          />
        </Box>
        {/* Our Mission Section */}
        <OurMission />

        {/* Honors that inspire us Section */}
        <Box w="full" py={0} mt={8}>
          <VStack spacing={2} align="start">
            <Heading
              textAlign="center"
              w="full"
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              color="#000B8C"
              lineHeight="1.2"
            >
              Honors{" "}
              <Text as="span" color="gray.700">
                that inspire us
              </Text>
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.500"
              textAlign="center"
              fontStyle="italic"
              w="full"
            >
              Recognition for Innovation, Trust, and Impact
            </Text>

            {/* Description */}
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              textAlign="start"
              maxW="7xl"
              mt={4}
              lineHeight="1.2"
              px={{ base: 4, md: 0 }}
            >
              At Business Automation Limited, every accolade is more than an
              award—it's a recognition of the passion, precision, and purpose
              that define our work. These honors affirm our long-standing
              commitment to delivering technology solutions that truly matter.
            </Text>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              textAlign="start"
              maxW="7xl"
              mt={0}
              lineHeight="1.2"
              px={{ base: 4, md: 0 }}
            >
              Over the years, our projects have been nationally and
              internationally acclaimed for their innovation, impact, and
              excellence in transforming public services, digital governance,
              and enterprise operations.
            </Text>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              textAlign="start"
              maxW="7xl"
              mt={4}
              lineHeight="1.2"
              px={{ base: 4, md: 0 }}
            >
              We are proud to have been recognized by institutions, including:
            </Text>
            <VStack spacing={1} align="start" w="full" maxW="7xl">
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color="gray.700"
                fontWeight="medium"
                textAlign="start"
              >
                •{" "}
                <Text as="span" fontWeight="bold">
                  United Nations
                </Text>{" "}
                – for contributions to public service innovation
              </Text>
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color="gray.700"
                fontWeight="medium"
                textAlign="start"
              >
                •{" "}
                <Text as="span" fontWeight="bold">
                  e-Asia Awards
                </Text>{" "}
                – for excellence in open digital government
              </Text>
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color="gray.700"
                fontWeight="medium"
                textAlign="start"
              >
                •{" "}
                <Text as="span" fontWeight="bold">
                  BASIS National ICT Awards
                </Text>{" "}
                – multiple wins for Best IT Projects in Bangladesh
              </Text>
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color="gray.700"
                fontWeight="medium"
                textAlign="start"
              >
                •{" "}
                <Text as="span" fontWeight="bold">
                  Bangladesh Computer Society
                </Text>{" "}
                – for standout execution of mission-critical systems
              </Text>
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color="gray.700"
                fontWeight="medium"
                textAlign="start"
              >
                •{" "}
                <Text as="span" fontWeight="bold">
                  South Asian mBillionth Award
                </Text>{" "}
                – for mobile-driven business innovation.
              </Text>
            </VStack>

            {/* Awards Grid */}
            <VStack spacing={6} w="full" maxW="7xl" py={0}>
              {/* First Row - 3 images */}
              <Grid
                templateColumns={{
                  base: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                }}
                gap={6}
                w="full"
              >
                <Box
                  bg="white"
                  borderRadius="lg"
                  overflow="hidden"
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  aspectRatio={4 / 3}
                >
                  <Image
                    src="/png/Awards.png"
                    alt="Award Recognition 1"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    fallbackSrc="https://via.placeholder.com/400x300?text=Award+1"
                  />
                </Box>

                <Box
                  bg="white"
                  borderRadius="lg"
                  overflow="hidden"
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  aspectRatio={4 / 3}
                >
                  <Image
                    src="/png/Awards_1.png"
                    alt="Award Recognition 2"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    fallbackSrc="https://via.placeholder.com/400x300?text=Award+2"
                  />
                </Box>

                <Box
                  bg="white"
                  borderRadius="lg"
                  overflow="hidden"
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  aspectRatio={4 / 3}
                  gridColumn={{ base: "1 / -1", sm: "1 / -1", md: "auto" }}
                >
                  <Image
                    src="/png/Awards_2.png"
                    alt="Ministry of Land Bangladesh Recognition"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    fallbackSrc="https://via.placeholder.com/400x300?text=Ministry+Award"
                  />
                </Box>
              </Grid>

              {/* Second Row - 2 images centered */}
              <HStack spacing={6} justify="center" w="full">
                <Box
                  bg="white"
                  borderRadius="lg"
                  overflow="hidden"
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  aspectRatio={4 / 2}
                  maxW="xl"
                  w="full"
                >
                  <Image
                    src="/png/Awards_3.png"
                    alt="United Nations Recognition"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    fallbackSrc="https://via.placeholder.com/400x300?text=UN+Award"
                  />
                </Box>

                <Box
                  bg="white"
                  borderRadius="lg"
                  overflow="hidden"
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  aspectRatio={4 / 2}
                  maxW="xl"
                  w="full"
                >
                  <Image
                    src="/png/Awards_4.png"
                    alt="Excellence Award"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    fallbackSrc="https://via.placeholder.com/400x300?text=Excellence+Award"
                  />
                </Box>
                <Box
                  bg="white"
                  borderRadius="lg"
                  overflow="hidden"
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  aspectRatio={4 / 2}
                  maxW="xl"
                  w="full"
                >
                  <Image
                    src="/png/award_6.jpg"
                    alt="Excellence Award"
                    w="100%"
                    h="100%"
                    objectFit="contain"
                    fallbackSrc="https://via.placeholder.com/400x300?text=Excellence+Award"
                  />
                </Box>
              </HStack>
            </VStack>

            {/* Bottom Text */}
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color="gray.600"
              textAlign="start"
              maxW="7xl"
              lineHeight="1.2"
              px={{ base: 4, md: 0 }}
              mt={3}
            >
              These achievements reflect the trust of our clients, the strength
              of our partnerships, and the dedication of our people—who turn
              vision into impact every single day.
            </Text>
          </VStack>
        </Box>
        <OurLeaders data={data} />

        <Box w="full" py={0}>
          <Container maxW="7xl">
            <VStack spacing={6} align="center">
              {/* Section Header */}
              {/* <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color="#000B8C"
                textAlign="center"
                mb={0}
              >
                 
              </Heading> */}
              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="bold"
                color="#000B8C"
                lineHeight="1.2"
              >
                Customer{" "}
                <Text as="span" color="gray.700">
                  Engagement
                </Text>
              </Heading>

              {/* Description */}
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.600"
                textAlign="justify"
                maxW="7xl"
                lineHeight="1.2"
                px={{ base: 4, md: 0 }}
              >
                At Business Automation Limited, customer engagement doesn't end
                with deployment—it begins there. We're committed to ensuring
                long-term success through responsive support, user empowerment,
                and capacity-building initiatives that drive real outcomes for
                government and enterprise clients.
              </Text>
            </VStack>
          </Container>
        </Box>
        <Box
          flex={{ base: 1, lg: 1 }}
          display="flex"
          alignItems="center"
          pt={8}
          justifyContent="center"
          h="100%"
        >
          <Image
            src="/png/items.png"
            alt="Queue-Pro Illustration"
            maxW="100%"
            height="auto"
            borderRadius="lg"
            loading="lazy"
            fallbackSrc="/png/default-product.png"
          />
        </Box>
        {/* 24/7 Support Services Section */}
        <Box w="full" py={16}>
          <Container maxW="7xl">
            <VStack spacing={8} align="center">
              {/* Section Header */}
              <VStack spacing={4} align="center">
                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  textAlign="center"
                  mb={0}
                >
                  24/7{" "}
                  <Text as="span" color="#000B8C">
                    Support Services
                  </Text>
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.500"
                  textAlign="center"
                  fontStyle="italic"
                >
                  Reliability when it matters most
                </Text>
              </VStack>

              {/* Description Paragraphs */}
              <VStack spacing={0} align="center" maxW="7xl">
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  textAlign="justify"
                  lineHeight="1.2"
                >
                  We understand that mission-critical systems—from eGovernment
                  platforms to enterprise portals—must run smoothly, securely,
                  and without interruption. That's why we offer 24/7 technical
                  support, tailored to client requirements, to ensure
                  uninterrupted service delivery and rapid issue resolution.
                </Text>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  textAlign="justify"
                  lineHeight="1.2"
                >
                  Our dedicated support teams are trained to monitor performance
                  in real time, proactively detect disruptions, and resolve
                  issues efficiently—across digital platforms, nationwide
                  deployments, or citizen-facing applications.
                </Text>
              </VStack>

              {/* Key Features Header */}
              <Heading
                as="h3"
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                fontWeight="bold"
                pt={4}
                color="#000B8C"
                textAlign="center"
              >
                Key Features of Our Support Services:
              </Heading>

              {/* Features Grid */}
              <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                spacing={6}
                w="full"
                maxW="7xl"
              >
                {[
                  {
                    icon: "/svg/support_service_6.svg",
                    title:
                      "24/7 Multichannel Helpdesk: Phone, email, web portal, and on-site desk options",
                  },
                  {
                    icon: "/svg/support_service_4.svg",
                    title:
                      "Real-Time Monitoring & Alerts: For system uptime, usage, and performance",
                  },
                  {
                    icon: "/svg/support_service_3.svg",
                    title:
                      "SLA-Based Issue Management: With ticket tracking and escalation workflows",
                  },
                  {
                    icon: "/svg/support_service_5.svg",
                    title:
                      "Remote & On-Site Troubleshooting: Based on project tier and client needs",
                  },
                  {
                    icon: "/svg/support_service_2.svg",
                    title:
                      "Dedicated Technical Teams: Assigned to critical projects and platforms",
                  },
                  {
                    icon: "/svg/support_service_1.svg",
                    title:
                      "Knowledge Base Access: With manuals, FAQs, and self-service guidance",
                  },
                ].map((feature, index) => (
                  <Box
                    key={index}
                    bg="white"
                    p={6}
                    borderRadius="xl"
                    boxShadow="md"
                    border="1px"
                    borderColor="gray.200"
                    transition="transform 0.3s ease"
                    _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                  >
                    <HStack spacing={4} align="start">
                      <Box
                        w={12}
                        h={12}
                        // bg="#000B8C"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Image
                          src={feature.icon}
                          alt={`Support Service ${index + 1}`}
                          w={12}
                          h={12}
                          // filter="invert(1)" // Makes the SVG white
                        />
                      </Box>
                      <VStack spacing={2} align="start" flex="1">
                        <Text
                          fontSize={{ base: "sm", md: "md" }}
                          fontWeight="normal"
                          color="#000B8C"
                          lineHeight="1.2"
                        >
                          {feature.title}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>

              {/* Bottom Text */}
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.600"
                textAlign="justify"
                maxW="7xl"
                lineHeight="1.2"
              >
                We've successfully supported nationwide systems for ministries,
                licensing platforms, and high-volume digital services—with
                response teams that scale with your users.
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* System Activation & Training Section */}
        <Box w="full" py={8}>
          <Container maxW="7xl">
            <VStack spacing={6} align="center">
              {/* Section Header */}
              <VStack spacing={2} align="center">
                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  textAlign="center"
                  mb={0}
                >
                  System{" "}
                  <Text as="span" color="#000B8C">
                    Activation & Training
                  </Text>
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.500"
                  textAlign="center"
                  fontStyle="italic"
                >
                  Empowering people, not just platforms
                </Text>
              </VStack>

              {/* Description */}
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.600"
                textAlign="justify"
                maxW="7xl"
                lineHeight="1.2"
                px={{ base: 4, md: 0 }}
              >
                We believe technology adoption succeeds when users feel
                confident. That's why every project includes customized training
                programs to ensure administrators, government officials, and end
                users are fully prepared to manage, operate, and sustain the
                system. Our training is human-centered, role-specific, and
                designed to simplify even complex digital workflows. Whether
                it's a licensing system or a citizen portal, we ensure
                stakeholders are empowered from Day 1.
              </Text>

              {/* Key Features Header */}
              <Heading
                as="h3"
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                fontWeight="bold"
                color="#000B8C"
                textAlign="center"
              >
                Key Features of Our Support Services:
              </Heading>

              {/* Features Flex */}
              <Flex
                flexWrap="wrap"
                gap={6}
                w="full"
                maxW="7xl"
                justifyContent="center"
                alignItems="stretch"
              >
                {[
                  {
                    icon: "/svg/sat_1.svg",
                    title:
                      "Customized Modules based on project scope, roles, and digital maturity",
                  },
                  {
                    icon: "/svg/sat_2.svg",
                    title:
                      "Hands-On Practice through guided system walkthroughs and real scenarios",
                  },
                  {
                    icon: "/svg/sat_3.svg",
                    title:
                      "Government-Focused Training for officers, data entry teams, and supervisors",
                  },
                  {
                    icon: "/svg/sat_4.svg",
                    title: "Bilingual Delivery: Training in Bangla and English",
                  },
                  {
                    icon: "/svg/sat_5.svg",
                    title:
                      "Training Aids: Illustrated manuals, quick-start guides, and explainer videos",
                  },
                  {
                    icon: "/svg/sat_6.svg",
                    title:
                      "Remote & On-Site Options for urban and rural deployments",
                  },
                  {
                    icon: "/svg/sat_7.svg",
                    title: "Refresher & Follow-Up Sessions post go-live",
                  },
                ].map((feature, index) => (
                  <Box
                    key={index}
                    w={{
                      base: "100%",
                      md: "calc(50% - 12px)",
                      lg: "calc(33.333% - 16px)",
                    }}
                    maxW="400px"
                    minH="200px"
                  >
                    <VStack
                      spacing={3}
                      align="start"
                      textAlign="start"
                      p={4}
                      h="full"
                    >
                      <Box
                        w={16}
                        h={16}
                        // bg="#000B8C"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="2xl"
                      >
                        <Image
                          src={feature.icon}
                          alt={`Training Feature ${index + 1}`}
                          w={16}
                          h={16}
                          // filter="invert(1)" // Makes the SVG white
                        />
                      </Box>
                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        fontWeight="medium"
                        color="gray.700"
                        lineHeight="1.4"
                        textAlign="start"
                      >
                        {feature.title}
                      </Text>
                    </VStack>
                  </Box>
                ))}
              </Flex>
            </VStack>
          </Container>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutBusinessAutomation;
